/**
 * mazeFlowPersistence.ts
 * 迷路アプリのフロー（ノード・エッジ）をステージごとに localStorage へ保存/復元する
 * 既存の flow/persistence.ts と同じ考え方だが、ステージ単位のキーを使う
 */
import type { AppEdge, AppNode } from '../../flow/types';
import { isNodeKind } from '../../flow/nodeRegistry';

type PersistedMazeFlow = {
  version: 1;
  nodes: AppNode[];
  edges: AppEdge[];
};

const getStorageKey = (stageId: number) => `filo:maze-flow:v1:stage-${stageId}`;

export const saveMazeFlow = (stageId: number, nodes: AppNode[], edges: AppEdge[]): void => {
  const payload: PersistedMazeFlow = {
    version: 1,
    nodes: nodes.map((node) => ({ ...node, selected: undefined, dragging: undefined })) as AppNode[],
    edges: edges.map((edge) => ({ ...edge, selected: undefined, className: undefined })),
  };
  try {
    localStorage.setItem(getStorageKey(stageId), JSON.stringify(payload));
  } catch {
    // 容量超過などは保存を諦める
  }
};

export const loadMazeFlow = (stageId: number): { nodes: AppNode[]; edges: AppEdge[] } | null => {
  try {
    const raw = localStorage.getItem(getStorageKey(stageId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedMazeFlow;
    if (parsed.version !== 1 || !Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
      return null;
    }
    const nodes = parsed.nodes.filter((node) => node && typeof node.id === 'string' && isNodeKind(node.type));
    const hasStart = nodes.some((node) => node.id === 'start');
    const hasEnd = nodes.some((node) => node.id === 'end');
    if (!hasStart || !hasEnd) return null;
    const ids = new Set(nodes.map((node) => node.id));
    const edges = parsed.edges.filter((edge) => edge && ids.has(edge.source) && ids.has(edge.target));
    return { nodes, edges };
  } catch {
    return null;
  }
};
